import {
  Component,
  Event,
  EventEmitter,
  Fragment,
  h,
  Host,
  Prop,
  Watch,
} from "@stencil/core";
import { App, QuacInfo, ZapSuggestion } from "../../api/types";
import { LinkTarget } from "../../lib/link-target";
import ZapierRowShimmer from "../zapier-prefab-row/zap-row-shimmer";
import { ModalOpen } from "../../lib/modal";
import { zapierURL } from "../../api/config";

@Component({
  tag: "zapier-zap-suggestions",
  styleUrl: "zapier-zap-suggestions.css",
  shadow: true,
})
export class ZapierZapSuggestions {
  /** Load a static list of suggestions from a script tag with this ID  */
  @Prop() fromJson = "";

  /** fb_embed query string to include */
  @Prop() facebookEmbed = "";

  /** The maximum number of suggestions to show */
  @Prop() limit = 5;

  /** The suggestion to display */
  @Prop() zapTitle?: string;

  /** The Zap Template URL, either set by the user or Zap Template */
  @Prop() url?: string;

  /** The first app being selected */
  @Prop() firstApp?: App;

  /** The second app being selected */
  @Prop() secondApp?: App;

  /** Open links in a new tab or the same tab */
  @Prop() linkTarget: LinkTarget = "new-tab";

  /** Are the Zapier app suggestions loading? */
  @Prop() loadingSuggestions = false;

  /** Call to action text */
  @Prop() callToAction?: string;

  /** The app services used in the Zap */
  @Prop({ mutable: true }) suggestions: ZapSuggestion[] = [];

  /** Request to open a modal to the given URL */
  @Event() modalOpen!: EventEmitter<ModalOpen>;

  /** The HTTP referrer to use for links and events */
  @Prop() referrer = location.href;

  /** Information for QuAC */
  @Prop() quacInfo?: QuacInfo;

  private scrollView!: HTMLElement;

  componentWillLoad() {
    if (this.fromJson) {
      const element = document.getElementById(this.fromJson);
      if (!element) {
        throw new Error(`couldn't find #${this.fromJson}`);
      }
      this.suggestions = JSON.parse(element.textContent ?? "");
    }
  }

  @Watch("suggestions")
  scrollToBeginning() {
    if (!this.scrollView) {
      return;
    }
    this.scrollView.scrollLeft = 0;
  }

  private Suggestion = ({ suggestion }: { suggestion: ZapSuggestion }) => {
    return (
      <zapier-prefab-row
        fbEmbed={this.facebookEmbed}
        quacInfo={this.quacInfo}
        referrer={this.referrer}
        services={suggestion.services}
        firstApp={this.firstApp}
        secondApp={this.secondApp}
        linkTarget={this.linkTarget}
        url={suggestion.webintent_url}
        zapTitle={suggestion.title}
        onModalOpen={(event) => {
          event.stopPropagation();
          this.modalOpen.emit(event.detail);
        }}
        callToAction={this.callToAction}
      >
        <div slot="footer-start">
          <span class="zap-footer">{this.getSubtitle(suggestion)}</span>
        </div>
      </zapier-prefab-row>
    );
  };

  // A fallback Zap suggestion is something that looks like a Zap Template but
  // doesn't actually have a ZT behind the hood. It shows both apps on the
  // card/row but it just takes the user to an empty Zap Editor.
  private Fallback = () => {
    return (
      <zapier-prefab-row
        fbEmbed={this.facebookEmbed}
        quacInfo={this.quacInfo}
        referrer={this.referrer}
        firstApp={this.firstApp}
        secondApp={this.secondApp}
        linkTarget={this.linkTarget}
        url={this.url}
        zapTitle={this.zapTitle}
      >
        <div slot="footer-start">
          <span class="zap-footer">{this.getSubtitle(undefined)}</span>
        </div>
      </zapier-prefab-row>
    );
  };

  private Shimmer = () => {
    return <ZapierRowShimmer />;
  };

  private setScrollView = (element: HTMLElement | undefined) => {
    if (!element) {
      return;
    }
    this.scrollView = element;
  };

  render() {
    const suggestions = this.suggestions.slice(0, this.limit);
    return (
      <Host>
        <div class="scroll-view" ref={this.setScrollView}>
          {(() => {
            if (this.loadingSuggestions) {
              return (
                <Fragment>
                  {Array.from({ length: this.limit }, () => (
                    <this.Shimmer />
                  ))}
                </Fragment>
              );
            }
            if (suggestions.length > 0) {
              return (
                <Fragment>
                  {suggestions.map((suggestion) => (
                    <this.Suggestion suggestion={suggestion} />
                  ))}
                </Fragment>
              );
            }
            if (this.secondApp) {
              return <this.Fallback />;
            }
            if (this.firstApp) {
              const integrationURL = new URL("/apps", zapierURL);
              if (this.firstApp.slug) {
                integrationURL.pathname = `/apps/${this.firstApp.slug}/integrations`;
              }
              return (
                <div class="no-suggestions">
                  <div>No workflows found.</div>
                  <a href={integrationURL.href} target="_blank" class="button">
                    View {this.firstApp.name} integrations
                  </a>
                </div>
              );
            }
            return (
              <div class="no-suggestions">
                <div>No workflows found.</div>
              </div>
            );
          })()}
        </div>
      </Host>
    );
  }

  private getSubtitle(suggestion: ZapSuggestion | undefined): string {
    if (this.firstApp && this.secondApp) {
      return `${this.firstApp.name} + ${this.secondApp.name}`;
    }
    if (suggestion?.services) {
      return suggestion.services.map((s) => s.name).join(" + ");
    }
    return "";
  }
}
